import { useState } from 'react'
import { UserPlus, Loader2 } from '@/ui/icons'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '@/ui/components'
import { Button, Input, Label } from '@/ui/components'
import { TripService } from '@/modules/trips'
import { useAuth } from '@/modules/auth'
import type { AddTagAlongDto } from '@/shared/api'

interface AddTagAlongModalProps {
  isOpen: boolean
  onClose: () => void
  tripId: string
  parentMemberId: string
  parentMemberName: string
  onAdded?: () => void
}

export function AddTagAlongModal({
  isOpen,
  onClose,
  tripId,
  parentMemberId,
  parentMemberName,
  onAdded
}: AddTagAlongModalProps) {
  const { getAccessToken } = useAuth()
  const [guestName, setGuestName] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setGuestName('')
      setError(null)
      onClose()
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    const name = guestName.trim()
    if (!name) {
      setError('Name is required')
      return
    }

    setIsSaving(true)
    try {
      const service = new TripService(getAccessToken)
      const data: AddTagAlongDto = { guest_name: name }
      await service.addTagAlong(tripId, parentMemberId, data)
      setGuestName('')
      onAdded?.()
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add tag-along')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            Add plus-one
          </DialogTitle>
          <DialogDescription>
            Add a guest, family member, or friend who will be riding with {parentMemberName}. They won&apos;t count toward the vehicle limit.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="tag-along-name">Name</Label>
            <Input
              id="tag-along-name"
              value={guestName}
              onChange={(e) => setGuestName(e.target.value)}
              placeholder="e.g. Jane, Kids"
              disabled={isSaving}
              autoFocus
            />
          </div>
          {error && (
            <p className="text-sm text-destructive">{error}</p>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={isSaving}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Adding...
                </>
              ) : (
                'Add'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
